import type { ReactNode } from 'react';
import { buildQuestionDisplayText } from '@padcanvas/shared';
import LatexText from '../latex/LatexText';
import './QuestionLine.css';

export interface QuestionLineData {
  id: string;
  questionText: string;
  subject?: string;
  difficulty?: string;
  questionType?: string | null;
  /** STRUCTURED sub-parts (id + statement) so the row shows stem + parts. */
  parts?: Array<{ partId: string; statement?: string }> | null;
  module?: { id: string; name: string } | null;
  appearedInExams?: { exam: string; date?: string }[] | null;
  tags?: string[];
}

interface QuestionLineProps {
  question: QuestionLineData;
  onClick?: (questionId: string) => void;
  showArrow?: boolean;
  /** Rendered at the end of the row (before arrow), visible on hover */
  renderHoverAction?: (questionId: string) => ReactNode;
  /** Rendered at the start of the row (before content). Always visible. */
  renderLeading?: (questionId: string) => ReactNode;
  /** Extra class(es) on the row root — e.g. for status-based background tinting. */
  className?: string;
  /** Exams the viewing student is targeting (e.g. `['ICSE']`). When provided,
   *  the previous-year (PYQ) badge only reflects appearances in these exams, so
   *  an ICSE student never sees a "CBSE 2023" tag. Omit (admin/teacher usage) to
   *  show the first appearance regardless of exam. */
  targetExams?: string[];
}

function wantedExamSet(targetExams?: string[]): Set<string> | null {
  if (!targetExams || targetExams.length === 0) return null;
  return new Set(targetExams.map(e => e.trim().toUpperCase()));
}

/**
 * True when the question is a previous-year question (PYQ) for the given exams —
 * i.e. it appeared in at least one of `targetExams`. With no `targetExams`, any
 * recorded appearance counts. Shared by the badge and the "PYQ only" filters so
 * the "is this a PYQ for me" rule lives in one place.
 */
export function isPyqForExams(
  appearedInExams: { exam: string; date?: string }[] | null | undefined,
  targetExams?: string[],
): boolean {
  if (!appearedInExams || appearedInExams.length === 0) return false;
  const wanted = wantedExamSet(targetExams);
  if (!wanted) return true;
  return appearedInExams.some(a => a.exam && wanted.has(a.exam.trim().toUpperCase()));
}

function getExamLabel(question: QuestionLineData, targetExams?: string[]): string | null {
  if (!question.appearedInExams || question.appearedInExams.length === 0) return null;
  let appearances = question.appearedInExams;
  // Scope the PYQ badge to the student's target exam(s) when known — don't show
  // e.g. a CBSE tag to a student targeting only ICSE.
  const wanted = wantedExamSet(targetExams);
  if (wanted) {
    appearances = appearances.filter(a => a.exam && wanted.has(a.exam.trim().toUpperCase()));
    if (appearances.length === 0) return null;
  }
  const first = appearances[0];
  const year = first.date ? new Date(first.date).getFullYear() : null;
  return year ? `${first.exam} ${year}` : first.exam;
}

function formatQuestionType(type: string): string {
  if (type === 'mcq') return 'MCQ';
  return type.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function QuestionLine({ question, onClick, showArrow = true, renderHoverAction, renderLeading, className, targetExams }: QuestionLineProps) {
  const examDisplayLabel = getExamLabel(question, targetExams);

  return (
    <div
      className={`question-line ${onClick ? 'clickable' : ''} ${className ?? ''}`}
      onClick={onClick ? () => onClick(question.id) : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {renderLeading && (
        <span className="ql-leading">{renderLeading(question.id)}</span>
      )}
      <div className="ql-content">
        <div className="ql-pills">
          {question.questionType && (
            <span className="ql-pill ql-type">{formatQuestionType(question.questionType)}</span>
          )}
          {question.module?.name && (
            <span className="ql-pill ql-module">{question.module.name}</span>
          )}
          {examDisplayLabel && (
            <span className="ql-pill ql-exam">{examDisplayLabel}</span>
          )}
          {question.tags && question.tags.length > 0 && (
            <>
              <span className="ql-separator">|</span>
              {question.tags.map(tag => (
                <span key={tag} className="ql-pill ql-tag">{tag}</span>
              ))}
            </>
          )}
        </div>
        <span className="ql-text">
          <LatexText text={buildQuestionDisplayText(question)} truncate={140} expandInline />
        </span>
      </div>
      {renderHoverAction && (
        <span className="ql-hover-action" onClick={e => { e.preventDefault(); e.stopPropagation(); }}>
          {renderHoverAction(question.id)}
        </span>
      )}
      {showArrow && (
        <svg className="ql-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      )}
    </div>
  );
}
